'use client';

import { productShareLinksSX } from '@/components/blocks/ProductDetails/styles/productShareLinks';
import DynamicIcon from '@/components/icons/DynamicIcon';
import { useProductDetails } from '@/data/Content/ProductDetails';
import { useSkuListTable } from '@/data/Content/SkuListTable';
import { ContentContext } from '@/data/context/content';
import { useLocalization } from '@/data/Localization';
import { useShareLinks } from '@/utils/useShareLinks';
import { Close } from '@mui/icons-material';
import LinkIcon from '@mui/icons-material/Link';
import { Box, IconButton, Snackbar, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useContext, useState } from 'react';
import { MATERIAL_SYMBOL_ICONS } from 'shared/custom/constants/commonConstants';

const CopyLink = () => {
	const theme = useTheme();
	const { CopyLink, LinkCopiedToClipboard } = useLocalization('ProductShareLink');
	const [openSnackBarCopyLink, setOpenSnackBarCopyLink] = useState(false);
	const handleCopyLink = async () => {
		await navigator.clipboard.writeText(window.location.href);
		setOpenSnackBarCopyLink(true);
	};

	return (
		<>
			<Box
				className="share-product"
				sx={productShareLinksSX.contentCopyIcon}
				onClick={handleCopyLink}
			>
				<DynamicIcon
					name={MATERIAL_SYMBOL_ICONS.contentCopy}
					color={theme.palette.secondary.main}
					variant="filled"
				/>
				<Typography variant="body1" color="secondary.main">
					{CopyLink.t()}
				</Typography>
			</Box>
			<Snackbar
				anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
				open={openSnackBarCopyLink}
				autoHideDuration={3000}
				onClose={() => setOpenSnackBarCopyLink(false)}
				message={
					<Box display="flex" alignItems="center" width="100%">
						<LinkIcon sx={productShareLinksSX.linkIcon} />
						<Typography variant="body1">{LinkCopiedToClipboard.t()}</Typography>
						<IconButton size="small" color="inherit" onClick={() => setOpenSnackBarCopyLink(false)}>
							<Close fontSize="small" />
						</IconButton>
					</Box>
				}
			/>
		</>
	);
};

export const ProductShareLinks = () => {
	const { ShareProduct } = useLocalization('ProductShareLink');
	const { product } = useContext(ContentContext) as ReturnType<typeof useProductDetails> &
		ReturnType<typeof useSkuListTable>;
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down('md'));

	const { shareOptions: ShareSocialNetworks } = useShareLinks(product?.partNumber);

	return (
		<Box sx={productShareLinksSX.productShareLinksContainer} id="mediaShare">
			<DynamicIcon name={MATERIAL_SYMBOL_ICONS.share} size={48} />
			<Typography
				sx={productShareLinksSX.alignShareItem}
				variant={!isMobile ? 'headingHeadline' : 'headingTitle'}
			>
				{ShareProduct.t()}
			</Typography>
			<Box
				sx={
					!isMobile
						? productShareLinksSX.iconsContainerDesktop
						: productShareLinksSX.iconsContainerMobile
				}
			>
				{ShareSocialNetworks.map(({ materialIcon, name, onClick }) => (
					<Box
						sx={productShareLinksSX.iconContainer}
						onClick={onClick}
						key={name}
						className="share-product"
					>
						<DynamicIcon
							name={materialIcon}
							color={theme.palette.secondary.main}
							viewBox="0 0 24 24"
							className="share-product"
						/>
						<Typography variant="body1" color="secondary.main">
							{name}
						</Typography>
					</Box>
				))}
				<CopyLink />
			</Box>
		</Box>
	);
};
